import {
    HttpErrorResponse,
    HttpEvent,
    HttpEventType,
    HttpHandler,
    HttpHeaderResponse,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
    HttpResponse,
    HttpSentEvent,
    HttpUserEvent
} from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { CommonService } from '@common/services/common.service';
import { LanguageIds } from '@common/services/i18n.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { BehaviorSubject, NEVER, Observable, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
    private activeRequests = 0;
    private refreshingToken = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private loadingBar: any;

    constructor(
        private errorHandler: ErrorHandler,
        private loadingBarService: LoadingBarService,
        private commonService: CommonService
    ) {
        this.loadingBar = this.loadingBarService.useRef('http');
    }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
        this.loadingBar.start();

        req = this.setHeaders(req);

        this.activeRequests++;

        return next.handle(req).pipe(
            mergeMap((event: HttpEvent<any>) => {
                if (event.type === HttpEventType.Response) {
                    this.decreaseActiveRequests();
                }
                return [event];
            }),
            catchError((error) => {
                this.decreaseActiveRequests();

                if (this.shouldRefreshToken(error)) {
                    return this.refreshToken(req, next);
                }

                if (this.shouldLogout(error)) {
                    // In case the user logged out from another window
                    this.commonService.userService.logout();
                    return NEVER;
                }

                if (error?.error?.validationErrors || error?.error?.entityErrors) this.errorHandler.handleError(error);
                return throwError(() => error);
            })
        );
    }

    private shouldRefreshToken(error: any) {
        return (
            error instanceof HttpErrorResponse &&
            !error.url.endsWith('RefreshAccessToken') &&
            error.headers.has('Token-Expired') &&
            error.status === 401
        );
    }

    private shouldLogout(error: any) {
        return (
            error instanceof HttpErrorResponse &&
            !error.url.endsWith('RefreshAccessToken') &&
            !error.url.endsWith('Login') &&
            !error.headers.has('Token-Expired') &&
            error.status === 401
        );
    }

    private refreshToken(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.refreshingToken) {
            this.refreshingToken = true;
            this.refreshTokenSubject.next(null);

            return this.commonService.userService.refreshAccessToken().pipe(
                switchMap((token: any) => {
                    const currentUser = this.commonService.userService.currentUserSubject.getValue();
                    // If currentUser is not set, redirect is used to activate AuthGuard again. This solves issue of opening app after access token expires.
                    if (!currentUser) {
                        this.commonService.router.navigateByUrl('/');
                    }
                    this.refreshingToken = false;
                    this.refreshTokenSubject.next(token.accessToken);
                    return next.handle(this.setHeaders(request));
                }),
                catchError((error) => {
                    this.refreshingToken = false;
                    // logout only when the refresh token is invalid
                    if ([400, 401].includes(error.status)) {
                        this.commonService.userService.logout();
                    }

                    return throwError(() => error);
                })
            );
        }
        return this.refreshTokenSubject.pipe(
            filter((token) => token != null),
            take(1),
            switchMap(() => next.handle(this.setHeaders(request)))
        );
    }

    private setHeaders(request: HttpRequest<any>) {
        return request.clone({
            setHeaders: {
                Language: this.commonService.rememberStateService.get<LanguageIds>('languageId') || ''
            }
        });
    }

    private decreaseActiveRequests() {
        this.activeRequests--;

        if (this.activeRequests === 0) {
            this.loadingBar.complete();
        }
    }
}
